// frontend/src/pages/dashboard/TaskComponents/TaskFilterBar.js

import React from "react";
import {
  Paper,
  Box,
  TextField,
  IconButton,
  Tooltip,
  Autocomplete,
  Collapse,
  Button,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Filter as FilterIcon,
  FilterListOff as FilterOffIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { useMediaQuery, useTheme } from "@mui/material";

const TaskFilterBar = ({
  filters,
  setFilters,
  filtersOpen,
  setFiltersOpen,
  searchOpen,
  setSearchOpen,
  searchInputRef,
  taskStatuses,
  priorityOptions,
  users,
  projects,
  sprints,
  resetFilters,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const hasActiveFilters =
    filters.search ||
    filters.status ||
    filters.priority ||
    filters.assignee ||
    filters.project ||
    filters.sprint;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        p: { xs: 1.5, sm: 2 },
        mb: 3,
        borderRadius: 3,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 1.5 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: { xs: '100%', sm: 'auto' },
            flex: { xs: '1 1 100%', sm: '0 1 auto' },
          }}
        >
          {!searchOpen ? (
            <IconButton
              onClick={() => setSearchOpen(true)}
              size="medium"
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                width: 40,
                height: 40,
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  borderColor: 'primary.main',
                },
                transition: 'all 0.2s',
              }}
            >
              <SearchIcon />
            </IconButton>
          ) : (
            <TextField
              size="small"
              inputRef={searchInputRef}
              placeholder="Search tasks..."
              value={filters.search}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, search: e.target.value }))
              }
              onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        filters.search
                          ? setFilters((prev) => ({ ...prev, search: '' }))
                          : setSearchOpen(false)
                      }
                      sx={{ p: 0.5 }}
                    >
                      <CloseIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: 'primary.light' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
              }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  height: 40,
                },
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
            ml: { sm: 'auto' },
          }}
        >
          <Autocomplete
            size="small"
            options={taskStatuses}
            getOptionLabel={(opt) => opt.name || ''}
            value={taskStatuses.find((s) => s.key === filters.status) || null}
            onChange={(e, newValue) =>
              setFilters((prev) => ({ ...prev, status: newValue?.key || '' }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Status"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    height: 40,
                    minWidth: 130,
                  },
                }}
              />
            )}
            sx={{ minWidth: 130 }}
          />
          <Autocomplete
            size="small"
            options={priorityOptions}
            getOptionLabel={(opt) => opt.label || ''}
            value={priorityOptions.find((p) => p.value === filters.priority) || null}
            onChange={(e, newValue) =>
              setFilters((prev) => ({ ...prev, priority: newValue?.value || '' }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Priority"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    height: 40,
                    minWidth: 120,
                  },
                }}
              />
            )}
            sx={{ minWidth: 120 }}
          />
          <Autocomplete
            size="small"
            options={users}
            getOptionLabel={(opt) => opt.name || ''}
            value={users.find((u) => String(u.id) === String(filters.assignee)) || null}
            onChange={(e, newValue) =>
              setFilters((prev) => ({
                ...prev,
                assignee: newValue?.id ? String(newValue.id) : '',
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Assignee"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    height: 40,
                    minWidth: 140,
                  },
                }}
              />
            )}
            sx={{ minWidth: 140 }}
          />
          <Autocomplete
            size="small"
            options={projects}
            getOptionLabel={(opt) => opt.name || ''}
            value={projects.find((p) => String(p.id) === String(filters.project)) || null}
            onChange={(e, newValue) =>
              setFilters((prev) => ({
                ...prev,
                project: newValue?.id ? String(newValue.id) : '',
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Project"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    height: 40,
                    minWidth: 140,
                  },
                }}
              />
            )}
            sx={{ minWidth: 140 }}
          />
          <Autocomplete
            size="small"
            options={sprints}
            getOptionLabel={(opt) => opt.name || ''}
            value={sprints.find((s) => String(s.id) === String(filters.sprint)) || null}
            onChange={(e, newValue) =>
              setFilters((prev) => ({
                ...prev,
                sprint: newValue?.id ? String(newValue.id) : '',
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sprint"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    height: 40,
                    minWidth: 140,
                  },
                }}
              />
            )}
            sx={{ minWidth: 140 }}
          />
          <Tooltip title="Clear all filters">
            <IconButton
              onClick={resetFilters}
              size="medium"
              sx={{
                bgcolor: hasActiveFilters ? 'primary.light' : 'grey.200',
                color: hasActiveFilters ? 'primary.contrastText' : 'text.secondary',
                width: 40,
                height: 40,
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                },
                transition: 'all 0.2s',
              }}
            >
              <FilterOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            width: { xs: 'auto', sm: 'auto' },
          }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen(!filtersOpen)}
            sx={{
              borderRadius: 2,
              height: 40,
              textTransform: 'none',
              minWidth: 'auto',
              px: 2,
            }}
          >
            {filtersOpen ? 'Hide Filters' : 'Filters'}
          </Button>
        </Box>
      </Box>

      <Collapse in={filtersOpen} sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}>
          <Autocomplete
            size="small"
            options={taskStatuses}
            getOptionLabel={(opt) => opt.name || ''}
            value={taskStatuses.find((s) => s.key === filters.status) || null}
            onChange={(e, newValue) =>
              setFilters((prev) => ({ ...prev, status: newValue?.key || '' }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Status" size="small" />
            )}
            fullWidth
          />
          <Autocomplete
            size="small"
            options={["low", "medium", "high", "urgent"]}
            value={filters.priority || ""}
            onChange={(e, newValue) =>
              setFilters((prev) => ({ ...prev, priority: newValue || "" }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Priority" size="small" />
            )}
            fullWidth
          />
          <Autocomplete
            size="small"
            options={users}
            getOptionLabel={(opt) => opt.name || ''}
            value={users.find((u) => String(u.id) === String(filters.assignee)) || null}
            onChange={(e, newValue) =>
              setFilters((prev) => ({
                ...prev,
                assignee: newValue?.id ? String(newValue.id) : '',
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Assignee" size="small" />
            )}
            fullWidth
          />
          <Autocomplete
            size="small"
            options={projects}
            getOptionLabel={(opt) => opt.name || ''}
            value={projects.find((p) => String(p.id) === String(filters.project)) || null}
            onChange={(e, newValue) =>
              setFilters((prev) => ({
                ...prev,
                project: newValue?.id ? String(newValue.id) : '',
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Project" size="small" />
            )}
            fullWidth
          />
          <Autocomplete
            size="small"
            options={sprints}
            getOptionLabel={(opt) => opt.name || ''}
            value={sprints.find((s) => String(s.id) === String(filters.sprint)) || null}
            onChange={(e, newValue) =>
              setFilters((prev) => ({
                ...prev,
                sprint: newValue?.id ? String(newValue.id) : '',
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Sprint" size="small" />
            )}
            fullWidth
          />
          <Button
            size="small"
            variant="contained"
            onClick={resetFilters}
            fullWidth
            sx={{ borderRadius: 2, height: 40 }}
          >
            Clear All Filters
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default TaskFilterBar;